import { type ReactNode, useMemo, useState } from "react";
import { ChromeContext, type DesktopChrome } from "./ChromeContext";
import { DesktopHeader } from "./DesktopHeader";
import { HistorySidebar } from "./HistorySidebar";
import { PageLabelBand } from "./PageLabelBand";

/**
 * Desktop 用 Layout shell。
 *
 * 構造:
 * ```
 * <DesktopHeader> (brand + toolbar slot)
 * <PageLabelBand> ("カタログ" 等)
 * [<HistorySidebar> (任意)] [<main>{children}</main>]
 * ```
 *
 * page は `useDesktopChrome()` 経由で chrome (toolbar / pageLabel /
 * showHistorySidebar) を流し込む。
 */
interface Props {
  children: ReactNode;
}

export const DesktopLayout: React.FunctionComponent<Props> = ({
  children,
}: Props) => {
  const [chrome, setChrome] = useState<DesktopChrome>({});
  const ctxValue = useMemo(() => ({ chrome, setChrome }), [chrome]);

  return (
    <ChromeContext.Provider value={ctxValue}>
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <DesktopHeader
          toolbar={chrome.toolbar}
          showSidebarToggle={chrome.showHistorySidebar}
        />
        <PageLabelBand label={chrome.pageLabel} />
        <div className="flex-1 min-h-0 flex overflow-hidden">
          {chrome.showHistorySidebar && <HistorySidebar />}
          <main className="flex-1 min-h-0 overflow-y-auto">{children}</main>
        </div>
      </div>
    </ChromeContext.Provider>
  );
};
